import { Chain, getContract, prepareContractCall, sendTransaction, ThirdwebClient, toUnits, waitForReceipt } from "thirdweb";
import { Web3Config } from "../../config/Web3Config";
import { ILogger } from "../../../cityversetycoon/logger/ILogger";
import { Event } from "event-system";
import { Account, inAppWallet, InAppWalletAuth, smartWallet, SmartWalletConnectionOptions, Wallet } from "thirdweb/wallets";
import { ChainUtils } from "../../utils/ChainUtils";
import { approve as erc20Approve, decimals, getBalance } from "thirdweb/extensions/erc20";
import { symbol } from "thirdweb/extensions/common";
import { NFTPackage } from "../../nfts/NFTPackage";
import { TickerDelay } from "../../../tools/TickerDelay";
import { TokenStandart } from "../../TokenStandart";
import { burnBatch, claimTo as erc1155ClaimTo, mintWithSignature as erc1155mintWithSignature } from "thirdweb/extensions/erc1155";
import { NFT } from "../../nfts/NFT";
import { PromiseCompletionSource } from "../../../shared/async/PromiseCompletionSource";
import { NFTGiftPackage } from "../../nfts/NFTGiftPackage";
import { MintRequest } from "../MintRequest";
import { BlockchainTransactionResult } from "../../BlockchainTransactionResult";
import { PrepareMintResultDto } from "../../../cityversetycoon/networking/data/responses/marketplace/buildings/PrepareMintResultDto";
import { mintWithSignature as erc721mintWithSignature } from "thirdweb/extensions/erc721";
import { ThirdWebAnalytic } from "../../utils/ThirdWebAnalytic";

type Setter<T> = (value: T) => T;
type Getter<T> = () => T;
type PropertyAccessor<T> = { get: Getter<T>, set: Setter<T> };

export abstract class BaseInAppWallet<TConnectOptions> {
    private static readonly UNAUTH_ERROR_MESSAGE = "Missing auth token when validating user identity";
    private static readonly MAX_ATTEMP_COUNT = 1;
    private static readonly ATTEMPS_DELAY_MS = 500;

    public readonly onDataChanged: Event<void>;

    protected readonly embededWallet: Wallet<"inApp">;
    protected readonly smartWallet: Wallet<"smart">;
    protected readonly monSmartWallet: Wallet<"smart">;

    protected embededAccount: Account;
    protected smartAccount: Account;
    protected ethL1SmartAccount: Account;

    private previousUSDCBalance: string;
    private previousMONBalance: string;

    private smartWalletConntectPCS: PromiseCompletionSource<void>;
    private monSmartWalletConnectPCS: PromiseCompletionSource<void>;
    private disconnectPCS: PromiseCompletionSource<void>;

    public get connected(): boolean {
        return this.embededAccount != null && this.address != null;
    }

    public get address(): string {
        return this.embededAccount?.address;
    }

    public get supportedChainSelected(): boolean {
        return this.connected && this.smartWallet.getChain().id === this.web3Config.targetChain.id;
    }

    public get smartWalletAddress(): string {
        return this.smartAccount?.address;
    }

    public get chainId(): string {
        return this.connected ? ChainUtils.idToHex(this.embededWallet.getChain().id) : "unknown";
    }

    public get account(): Account {
        if (this.smartAccount == null) {
            this.logger.warn("Attemp of use smart account before initialization! Should be used \"waitSmartWalletConnection()\" before.");
        }
        return this.smartAccount;
    }

    public constructor(
        protected readonly client: ThirdwebClient,
        protected readonly web3Config: Web3Config,
        protected readonly thirdWebAnalytic: ThirdWebAnalytic,
        protected readonly logger: ILogger,
        authStrategy: Array<InAppWalletAuth>
    ) {
        this.onDataChanged = new Event();

        this.previousUSDCBalance = "0.0000";
        this.previousMONBalance = "0.0000";

        this.embededWallet = inAppWallet({
            auth: { options: authStrategy }
        });
        this.smartWallet = smartWallet({
            factoryAddress: this.web3Config.thirdWebFactoryAddress,
            chain: this.web3Config.targetChain,
            gasless: true
        });
        this.monSmartWallet = smartWallet({
            factoryAddress: this.web3Config.thirdWebFactoryAddress,
            chain: this.web3Config.ethL1Chain,
            gasless: true
        });

        this.onAccountChanged = this.onAccountChanged.bind(this);
        this.onAccountsChanged = this.onAccountsChanged.bind(this);
        this.onChainChanged = this.onChainChanged.bind(this);
        this.onDisconnect = this.onDisconnect.bind(this);

        this.embededWallet.subscribe("accountChanged", this.onAccountChanged);
        this.embededWallet.subscribe("accountsChanged", this.onAccountsChanged);
        this.embededWallet.subscribe("chainChanged", this.onChainChanged);
        this.embededWallet.subscribe("disconnect", this.onDisconnect);
        this.smartWallet.subscribe("accountChanged", this.onAccountChanged);
        this.smartWallet.subscribe("accountsChanged", this.onAccountsChanged);
        this.smartWallet.subscribe("chainChanged", this.onChainChanged);
        this.smartWallet.subscribe("disconnect", this.onDisconnect);
    }

    public abstract connect(options: TConnectOptions): Promise<boolean>;
    public abstract autoConnect(): Promise<boolean>;

    public async disconnect(): Promise<void> {
        try {
            if (!this.connected) {
                this.logger.debug("Unable to disconnect InAppWallet, already disconnected");
                return;
            }
            if (this.disconnectPCS != null) {
                return this.disconnectPCS.result;
            }
            this.disconnectPCS = new PromiseCompletionSource();
            await this.wrapOperation(() => Promise.all([this.smartWallet.disconnect(), this.embededWallet.disconnect()]), "disconnect wallets");
            this.disconnectPCS.complete();
            this.disconnectPCS = null;
        }
        catch (error: unknown) {
            this.disconnectPCS = null;
            this.logger.debug(`[InAppWallet] Exception on disconnect created wallet: ${error}`);
        }
        finally {
            this.smartAccount = null;
            this.embededAccount = null;
            this.smartWalletConntectPCS?.error(new Error("Wallet disconnected"));
            this.smartWalletConntectPCS = null;
            this.onDataChanged.emit();
        }
    }

    public async signMessage(nonce: string): Promise<string> {
        if (!this.connected) {
            this.logger.debug(`[BaseInAppWallet] Unable to sign message with created wallet, not connected`);
            return null;
        }
        return await this.wrapOperation(() => this.embededAccount.signMessage({ message: nonce }), "sign message");
    }

    public async updateUSDCBalance(): Promise<string> {
        if (!this.connected) {
            this.logger.warn("Update usdc balance for not connected wallet called!");
            return this.previousUSDCBalance;
        }
        try {
            const usdcContract = getContract({
                client: this.client,
                address: this.web3Config.reservoirDevUsdcCurrency,
                chain: this.web3Config.targetChain,
            });

            const result = await this.wrapOperation(() => getBalance({
                contract: usdcContract,
                address: this.smartWalletAddress ?? this.address,
            }), "update USDC balance");

            const usdcBalance = result?.displayValue;
            if (usdcBalance != null && usdcBalance.length > 0) {
                this.previousUSDCBalance = usdcBalance;
                return usdcBalance;
            }
            return this.previousUSDCBalance;
        }
        catch (e: unknown) {
            this.logger.debug(`Exception on get usdc balance request ${e}, will be provided old data`);
            return this.previousUSDCBalance;
        }
    }

    public async updateMONBalance(): Promise<string> {
        if (!this.connected) {
            this.logger.warn("Update MON balance for not connected wallet called!");
            return this.previousMONBalance;
        }
        try {
            const address = (this.smartWalletAddress ?? this.address) as `0x${string}`;
            const contract = getContract({
                client: this.client,
                chain: this.web3Config.ethL1Chain,
                address: this.web3Config.monCurrencyAddress
            });

            const result = await this.wrapOperation(() => getBalance({ contract, address: address }), "update MON balance");
            const monBalance = result?.displayValue;
            if (monBalance != null && monBalance.length > 0) {
                this.previousMONBalance = monBalance;
                return monBalance;
            }
            return this.previousMONBalance;
        }
        catch (e: unknown) {
            this.logger.debug(`Exception on get mon balance request ${e}, will be provided old data`);
            return this.previousMONBalance;
        }
    }

    public async updateErc20Balance(chain: Chain, erc20Address: string): Promise<string> {
        if (!this.connected) {
            this.logger.warn("Update ERC20 balance for not connected wallet called!");
            return this.previousMONBalance;
        }
        try {
            const address = (this.smartWalletAddress ?? this.address) as `0x${string}`;
            const contract = getContract({
                client: this.client,
                chain,
                address: erc20Address
            });

            const result = await getBalance({ contract, address: address });
            const erc20Balance = result?.displayValue;
            if (erc20Balance != null && erc20Balance.length > 0) {
                return erc20Balance;
            }
            return this.previousMONBalance;
        }
        catch (e: unknown) {
            this.logger.debug(`Exception on get erc20 balance request ${e}, will be provided old data`);
            return this.previousMONBalance;
        }
    }

    public async burnNFTPackage(nftPackage: NFTPackage): Promise<string> {
        if (!this.connected) {
            this.logger.debug(`Attemp of burn nft from not connected wallet`);
            return null;
        }
        try {
            await this.waitSmartWalletConnection();
            return await this.burnNFT(nftPackage, this.web3Config.packagesCollectionAddress);
        }
        catch (error: unknown) {
            if (await this.checkErrorForDisconnection(error)) {
                this.logger.debug("[BaseInAppWallet] Unable to burn nft, disconnected");
                return null;
            }
            this.logger.error(`Exception on burn package nft: `, error);
            return null;
        }
    }

    public async getTokenSymbol(chain: Chain, erc20Address: string): Promise<string> {
        if (!this.connected) {
            this.logger.warn("Get token symbol for not connected wallet called!");
            return null;
        }

        try {
            if (this.isTargetChain(chain)) {
                await this.waitSmartWalletConnection();
            } else {
                await this.waitEthL1SmartWalletConnection();
            }
            const contract = getContract({
                client: this.client,
                chain,
                address: erc20Address
            });

            const tokenSymbol = await symbol({contract})

            return tokenSymbol;
        }
        catch (error: unknown) {
            if (await this.checkErrorForDisconnection(error)) {
                this.logger.debug("[BaseInAppWallet] Unable to burn nft, disconnected");
                return null;
            }
            this.logger.error(`Exception on burn package nft: `, error);
            return null;
        }
    }

    public async burnNFTGiftPackage(nftGiftPackage: NFTGiftPackage): Promise<string> {
        if (!this.connected) {
            this.logger.debug(`Attemp of burn nft from not connected wallet`);
            return null;
        }
        try {
            await this.waitSmartWalletConnection();
            return await this.burnNFT(nftGiftPackage, this.web3Config.giftPackagesCollectionAddress);
        }
        catch (error: unknown) {
            if (await this.checkErrorForDisconnection(error)) {
                this.logger.debug("[BaseInAppWallet] Unable to burn nft, disconnected");
                return null;
            }
            this.logger.error(`Exception on burn gift package nft: `, error);
            return null;
        }
    }

    public async claimGiftNft(mintRequest: MintRequest): Promise<boolean> {
        try {
            const contract = getContract({
                client: this.client,
                address: this.web3Config.giftPackagesCollectionAddress,
                chain: this.web3Config.targetChain
            });

            const mintPayload = mintRequest.payload;
            const transaction = erc1155mintWithSignature({
                contract,
                payload: {
                    currency: mintPayload.currency as `0x${string}`,
                    pricePerToken: mintPayload.pricePerToken,
                    primarySaleRecipient: mintPayload.primarySaleRecipient as `0x${string}`,
                    quantity: mintPayload.quantity,
                    royaltyBps: mintPayload.royaltyBps,
                    royaltyRecipient: mintPayload.royaltyRecipient as `0x${string}`,
                    to: mintPayload.to as `0x${string}`,
                    tokenId: mintPayload.tokenId,
                    uid: mintPayload.uid,
                    uri: mintPayload.uri,
                    validityEndTimestamp: mintPayload.validityEndTimestamp,
                    validityStartTimestamp: mintPayload.validityStartTimestamp
                },
                signature: mintRequest.signature,
            });

            await this.waitSmartWalletConnection();
            const transactionResult = await this.wrapOperation(() => sendTransaction({
                account: this.smartAccount,
                transaction
            }), "claim gift NFT");
            this.logger.debug(`Transaction result: ${JSON.stringify(transactionResult, (_, v) => typeof v === 'bigint' ? v.toString() : v)}`);
            return true;
        }
        catch (error: unknown) {
            if (await this.checkErrorForDisconnection(error)) {
                this.logger.debug("Diconnected on claiming gift nft");
                return false;
            }
            this.logger.error(`Exception on claiming gift nft: `, error);
            return false;
        }
    }

    public async sendErc20Tokens(chain: Chain, erc20Address: string, address: string, value: number): Promise<boolean> {
        if (this.isTargetChain(chain)) {
            await this.waitSmartWalletConnection();
        } else {
            await this.waitEthL1SmartWalletConnection();
        }
        

        try {
            const tokenContract = getContract({
                client: this.client,
                chain,
                address: erc20Address
            });

            const tokenDecimals = await decimals({ contract: tokenContract });

            const transaction = prepareContractCall({
                contract: tokenContract,
                method: "function transfer(address to, uint256 amount) returns (bool)",
                params: [
                    address as `0x${string}`,
                    toUnits(value.toString(), tokenDecimals)
                ]
            });

            const tx = await sendTransaction({
                transaction,
                account: this.isTargetChain(chain) ? this.smartAccount : this.ethL1SmartAccount
            });

            if (tx !== null) {
                const receipt = await waitForReceipt(tx);
                this.logger.debug(`Send ERC20 tokens on ${this.isTargetChain(chain) ? "Base" : "Ethereum"} operation complete: ${receipt}`);
                return receipt.status == "success";
            }

            return false;
        }
        catch (e: unknown) {
            if (await this.checkErrorForDisconnection(e)) {
                this.logger.debug(`[InAppWallet] Unable to send ERC20 tokens on ${this.isTargetChain(chain) ? "Base" : "Ethereum"}, disconnected`);
                return null;
            }
            this.logger.debug(`Exception on sending ERC20 ${this.isTargetChain(chain) ? "Base" : "Ethereum"} tokens: ${e}`);
            return false;
        }
    }

    public async approveErc20Transaction(chain: Chain, erc20Address: string, spenderAddress: string, price: number): Promise<boolean> {
        if (this.isTargetChain(chain)) {
            await this.waitSmartWalletConnection();
        } else {
            await this.waitEthL1SmartWalletConnection();
        }

        try {
            const tokenContract = getContract({
                client: this.client,
                address: erc20Address,
                chain
            });
            const approveTx = erc20Approve({
                contract: tokenContract,
                spender: spenderAddress as `0x${string}`,
                amount: price
            });

            const tx = await sendTransaction({
                account: this.isTargetChain(chain) ? this.smartAccount : this.ethL1SmartAccount,
                transaction: approveTx
            });

            const receipt = await waitForReceipt(tx);
            this.logger.debug(`Approve transaction of ${erc20Address} on ${this.isTargetChain(chain) ? "Base" : "Ethereum"} result: ${JSON.stringify(receipt, (_, v) => typeof v === 'bigint' ? v.toString() : v)}`);
            return receipt.status == "success";
        }
        catch (e: unknown) {
            if (await this.checkErrorForDisconnection(e)) {
                this.logger.debug(`[InAppWallet] Unable to approve transaction on ${this.isTargetChain(chain) ? "Base" : "Ethereum"}, disconnected`);
                return null;
            }
            this.logger.debug(`Exception on approve of ${erc20Address} transaction on ${this.isTargetChain(chain) ? "Base" : "Ethereum"}: ${e}`);
            return false;
        }
    }

    public async processErc20StorePackageClaimTransaction(chain: Chain, tokenId: bigint): Promise<BlockchainTransactionResult> {
        if (this.isTargetChain(chain)) {
            await this.waitSmartWalletConnection();
        } else {
            await this.waitEthL1SmartWalletConnection();
        }

        try {
            const collectionContract = getContract({
                client: this.client,
                address: this.isTargetChain(chain) ? this.web3Config.packagesCollectionAddress : this.web3Config.l1PackageCollectionAddress,
                chain
            });

            const transaction = erc1155ClaimTo({
                contract: collectionContract,
                to: this.smartWalletAddress as `0x${string}`,
                tokenId: tokenId,
                quantity: BigInt(1)
            });

            const tx = await sendTransaction({
                account: this.isTargetChain(chain) ? this.smartAccount : this.ethL1SmartAccount,
                transaction
            });

            const receipt = await waitForReceipt(tx);
            this.logger.debug(`Store Package claim transaction receipt on ${this.isTargetChain(chain) ? "Base" : "Ethereum"}: ${JSON.stringify(receipt, (_, v) => typeof v === 'bigint' ? v.toString() : v)}`);
            return new BlockchainTransactionResult(receipt);
        }
        catch (e: unknown) {
            if (await this.checkErrorForDisconnection(e)) {
                this.logger.debug(`[InAppWallet] Unable to process store package purchase transaction on ${this.isTargetChain(chain) ? "Base" : "Ethereum"}, disconnected`);
                return null;
            }
            this.logger.debug(`Exception on processing store package claim transaction on ${this.isTargetChain(chain) ? "Base" : "Ethereum"}: ${e}`);
            return new BlockchainTransactionResult(null);;
        }
    }

    public async sendL1Erc20Tokens(erc20Address: string, address: string, value: number): Promise<boolean> {
        await this.waitEthL1SmartWalletConnection();

        try {
            const tokenContract = getContract({
                client: this.client,
                chain: this.web3Config.ethL1Chain,
                address: erc20Address
            });

            const tokenDecimals = await this.wrapOperation(() => decimals({ contract: tokenContract }), "send l1 erc20 tokens - decimals");

            const transaction = prepareContractCall({
                contract: tokenContract,
                method: "function transfer(address to, uint256 amount) returns (bool)",
                params: [
                    address as `0x${string}`,
                    toUnits(value.toString(), tokenDecimals)
                ]
            });

            const tx = await sendTransaction({
                transaction,
                account: this.ethL1SmartAccount
            });

            if (tx !== null) {
                const receipt = await this.wrapOperation(() => waitForReceipt(tx), "send l1 erc20 tokens - wait for Receipt");
                this.logger.debug(`Send ERC20 (L1) tokens operation complete: ${receipt}`);
                return receipt.status == "success";
            }

            return false;
        }
        catch (e: unknown) {
            if (await this.checkErrorForDisconnection(e)) {
                this.logger.debug("[InAppWallet] Unable to send Mon tokens, disconnected");
                return null;
            }
            this.logger.debug(`Exception on sending ERC20 (L1) tokens: ${e}`);
            return false;
        }
    }

    public async approveL1Erc20Transaction(erc20Address: string, price: number): Promise<boolean> {
        await this.waitEthL1SmartWalletConnection();

        try {
            const tokenContract = getContract({
                client: this.client,
                address: erc20Address,
                chain: this.web3Config.ethL1Chain
            });

            const approveTx = erc20Approve({
                contract: tokenContract,
                spender: this.web3Config.l1PackageCollectionAddress as `0x${string}`,
                amount: price
            });

            const tx = await this.wrapOperation(() => sendTransaction({
                account: this.ethL1SmartAccount,
                transaction: approveTx
            }), "approve l1 erc20 transaction - send transaction");

            const receipt = await this.wrapOperation(() => waitForReceipt(tx), "approve l1 erc20 transaction - wait for receipt");
            this.logger.debug(`Approve transaction result: ${JSON.stringify(receipt, (_, v) => typeof v === 'bigint' ? v.toString() : v)}`);
            return receipt.status == "success";
        }
        catch (e: unknown) {
            if (await this.checkErrorForDisconnection(e)) {
                this.logger.debug("[InAppWallet] Unable to approve Mon transaction, disconnected");
                return null;
            }
            this.logger.debug(`Exception on approve Mon transaction: ${e}`);
            return false;
        }
    }

    public async processL1StorePackageClaimTransaction(tokenId: bigint): Promise<BlockchainTransactionResult> {
        await this.waitEthL1SmartWalletConnection();

        try {
            const collectionContract = getContract({
                client: this.client,
                address: this.web3Config.l1PackageCollectionAddress,
                chain: this.web3Config.ethL1Chain
            });

            const transaction = erc1155ClaimTo({
                contract: collectionContract,
                to: this.smartWalletAddress as `0x${string}`,
                tokenId: tokenId,
                quantity: BigInt(1)
            });

            const tx = await sendTransaction({
                account: this.ethL1SmartAccount,
                transaction
            });

            const receipt = await this.wrapOperation(() => waitForReceipt(tx), "process l1 store package claim transaction - wait for receipt");
            this.logger.debug(`Mon transaction receipt: ${JSON.stringify(receipt, (_, v) => typeof v === 'bigint' ? v.toString() : v)}`);
            return new BlockchainTransactionResult(receipt);
        }
        catch (e: unknown) {
            if (await this.checkErrorForDisconnection(e)) {
                this.logger.debug("[InAppWallet] Unable to process Mon transaction, disconnected");
                return null;
            }
            this.logger.debug(`Exception on processing Mon transaction: ${e}`);
            return new BlockchainTransactionResult(null);;
        }
    }

    public async ethL1BuildingClaimWithSignature(mintRequest: PrepareMintResultDto): Promise<BlockchainTransactionResult> {
        try {
            const contract = getContract({
                client: this.client,
                address: this.web3Config.l1BuildingsContractAddress,
                chain: this.web3Config.ethL1Chain
            });

            const mintReq = mintRequest.mintRequest;

            const tokenContract = getContract({
                client: this.client,
                address: mintReq.currency,
                chain: this.web3Config.ethL1Chain
            });


            const approveTx = erc20Approve({
                contract: tokenContract,
                spender: this.web3Config.l1BuildingsContractAddress as `0x${string}`,
                amount: Number(mintReq.price) / 10 ** 18
            });

            const approveTxResult = await this.wrapOperation(() => sendTransaction({
                account: this.ethL1SmartAccount,
                transaction: approveTx
            }), "eth l1 building claim with signature - approve transaction");

            const approveReceipt = await this.wrapOperation(() => waitForReceipt(approveTxResult), "eth l1 building claim with signature - wait for receipt (approve)");

            if (approveReceipt.status != "success") {
                return new BlockchainTransactionResult(null);
            }

            const transaction = erc721mintWithSignature({
                contract,
                payload: {
                    to: this.ethL1SmartAccount.address as `0x${string}`,
                    royaltyRecipient: mintReq.royaltyRecipient as `0x${string}`,
                    royaltyBps: BigInt(mintReq.royaltyBps),
                    primarySaleRecipient: mintReq.primarySaleRecipient as `0x${string}`,
                    uri: mintReq.uri,
                    price: BigInt(mintReq.price),
                    currency: this.web3Config.monCurrencyAddress as `0x${string}`,
                    validityStartTimestamp: BigInt(mintReq.validityStartTimestamp),
                    validityEndTimestamp: BigInt(mintReq.validityEndTimestamp),
                    uid: mintReq.uid as `0x${string}`
                },
                signature: mintRequest.signature as `0x${string}`,
            });

            const tx = await sendTransaction({
                account: this.ethL1SmartAccount,
                transaction
            });

            const receipt = await this.wrapOperation(() => waitForReceipt(tx), "eth l1 building claim with signature - wait for receipt");
            return new BlockchainTransactionResult(receipt);
        }
        catch (e: unknown) {
            if (await this.checkErrorForDisconnection(e)) {
                this.logger.debug("[InAppWallet] Unable to claim with signature, disconnected");
                return null;
            }
            this.logger.debug(`Exception on claim with signature: ${e}`);
            return new BlockchainTransactionResult(null);
        }
    }

    public async erc20BuildingClaimWithSignature(chain: Chain, mintRequest: PrepareMintResultDto): Promise<BlockchainTransactionResult> {
        if (this.isTargetChain(chain)) {
            await this.waitSmartWalletConnection();
        } else {
            await this.waitEthL1SmartWalletConnection();
        }
        try {
            const contract = getContract({
                client: this.client,
                address: this.web3Config.buildingCollectionAddress,
                chain
            });

            const mintReq = mintRequest.mintRequest;

            const tokenContract = getContract({
                client: this.client,
                address: mintReq.currency,
                chain
            });


            const approveTx = erc20Approve({
                contract: tokenContract,
                spender: this.web3Config.buildingCollectionAddress as `0x${string}`,
                amount: Number(mintReq.price)
            });

            const approveTxResult = await this.wrapOperation(() => sendTransaction({
                account: this.isTargetChain(chain) ? this.smartAccount : this.ethL1SmartAccount,
                transaction: approveTx
            }), "building claim with signature - approve transaction");

            const approveReceipt = await this.wrapOperation(() => waitForReceipt(approveTxResult), "building claim with signature - wait for receipt (approve)");

            if (approveReceipt.status != "success") {
                return new BlockchainTransactionResult(null);
            }

            const transaction = erc721mintWithSignature({
                contract,
                payload: {
                    to: (this.isTargetChain(chain) ? this.smartAccount.address : this.ethL1SmartAccount.address) as `0x${string}`,
                    royaltyRecipient: mintReq.royaltyRecipient as `0x${string}`,
                    royaltyBps: BigInt(mintReq.royaltyBps),
                    primarySaleRecipient: mintReq.primarySaleRecipient as `0x${string}`,
                    uri: mintReq.uri,
                    price: BigInt(mintReq.price),
                    currency: mintReq.currency as `0x${string}`,
                    validityStartTimestamp: BigInt(mintReq.validityStartTimestamp),
                    validityEndTimestamp: BigInt(mintReq.validityEndTimestamp),
                    uid: mintReq.uid as `0x${string}`
                },
                signature: mintRequest.signature as `0x${string}`,
            });

            const tx = await sendTransaction({
                account: this.isTargetChain(chain) ? this.smartAccount : this.ethL1SmartAccount,
                transaction
            });

            const receipt = await this.wrapOperation(() => waitForReceipt(tx), "building claim with signature - wait for receipt");
            return new BlockchainTransactionResult(receipt);
        }
        catch (e: unknown) {
            if (await this.checkErrorForDisconnection(e)) {
                this.logger.debug("[InAppWallet] Unable to claim with signature, disconnected");
                return null;
            }
            this.logger.debug(`Exception on claim with signature: ${e}`);
            return new BlockchainTransactionResult(null);
        }
    }

    public async waitSmartWalletConnection(): Promise<void> {
        if (this.smartAccount != null) {
            return;
        }
        if (this.smartWalletConntectPCS == null) {
            if (this.embededAccount == null) {
                throw new Error("[BaseInAppWallet] Attemp of connect smart wallet before embeded account initialization");
            }
            this.logger.warn("Smart account connection not provided, attemp to connect");
            this.connectDefaultSmartAccount();
        }
        await this.smartWalletConntectPCS.result;
    }

    public async waitEthL1SmartWalletConnection(): Promise<void> {
        if (this.ethL1SmartAccount != null) {
            return;
        }
        if (this.monSmartWalletConnectPCS == null) {
            if (this.embededAccount == null) {
                throw new Error("[BaseInAppWallet] Attemp of connect smart wallet before embeded account initialization");
            }
            this.logger.warn("Smart account connection not provided, attemp to connect");
            this.connectEthL1SmartAccount();
        }
        await this.monSmartWalletConnectPCS.result;
    }

    protected async checkErrorForDisconnection(error: unknown): Promise<boolean> {
        if ((error instanceof Error && error.message.includes(BaseInAppWallet.UNAUTH_ERROR_MESSAGE))
            || (typeof error === "string" && error.includes(BaseInAppWallet.UNAUTH_ERROR_MESSAGE))
        ) {
            this.logger.error(`Disconnection from In-App wallet. Current state: {connected: ${this.connected}, smartAccountConnection: ${this.smartAccount != null}}. Reason: `, error);
            this.thirdWebAnalytic.trackMissingAuthTokenEvent();
            await this.disconnect();
            return true;
        }
        return false;
    }

    protected async wrapOperation<T>(operation: () => Promise<T>, operationTag?: string, attemp: number = 0): Promise<T> {
        try {
            return await operation();
        }
        catch (e: unknown) {
            if (attemp < BaseInAppWallet.MAX_ATTEMP_COUNT) {
                this.logger.debug(`Exception on ${operationTag ?? "wrap operation"}, attemp #${attemp}: ${e}`);
                await TickerDelay.delay(BaseInAppWallet.ATTEMPS_DELAY_MS);
                return await this.wrapOperation(operation, operationTag, ++attemp);
            }
            throw e;
        }
    }

    protected async connectDefaultSmartAccount(autoConnect?: boolean): Promise<void> {
        return await this.wrapOperation(() => this.connectSmartAccount(
            this.smartWallet,
            {
                get: () => {
                    return this.smartAccount;
                },
                set: (value: Account) => {
                    return this.smartAccount = value;
                }
            },
            {
                get: () => {
                    return this.smartWalletConntectPCS;
                },
                set: (value: PromiseCompletionSource<void>) => {
                    return this.smartWalletConntectPCS = value;
                }
            },
            this.web3Config.targetChain,
            autoConnect
        ), "connect default smart account");
    }

    protected async connectEthL1SmartAccount(autoConnect?: boolean): Promise<void> {
        return await this.wrapOperation(() => this.connectSmartAccount(
            this.smartWallet,
            {
                get: () => {
                    return this.ethL1SmartAccount;
                },
                set: (value: Account) => {
                    return this.ethL1SmartAccount = value;
                }
            },
            {
                get: () => {
                    return this.monSmartWalletConnectPCS;
                },
                set: (value: PromiseCompletionSource<void>) => {
                    return this.monSmartWalletConnectPCS = value;
                }
            },
            this.web3Config.ethL1Chain,
            autoConnect
        ), "connect eth l1 smart account");
    }

    private async connectSmartAccount(wallet: Wallet, accountAccessor: PropertyAccessor<Account>, pcsAccessor: PropertyAccessor<PromiseCompletionSource<void>>, chain: Chain, autoConnect?: boolean): Promise<void> {
        if (this.embededAccount == null) {
            throw new Error("[BaseInAppWallet] Unable to create smart account without embeded account");
        }
        if (accountAccessor.get() != null) {
            return;
        }
        if (pcsAccessor.get() != null) {
            return await pcsAccessor.get().result;
        }
        const pcs = pcsAccessor.set(new PromiseCompletionSource());
        const connectOptions: SmartWalletConnectionOptions = {
            client: this.client,
            personalAccount: this.embededAccount,
            chain: chain
        };
        const connectionPromise = this.wrapOperation(() => autoConnect ? wallet.autoConnect(connectOptions) : wallet.connect(connectOptions), "wallet connect");
        connectionPromise.then((result: Account) => {
            accountAccessor.set(result);
            pcs.complete();
            pcsAccessor.set(null);
        }).then((reason: unknown) => {
            pcs.error(reason);
            pcsAccessor.set(null);
        });
        return await pcs.result;
    }

    private async burnNFT(nft: NFT, collection: string): Promise<string> {
        if (nft.type !== TokenStandart.ERC1155) {
            this.logger.warn(`Attemp of burn ${nft.type} nft`);
            return null;
        }
        this.logger.debug(`burn nft ${JSON.stringify(nft, (_, v) => typeof v === 'bigint' ? v.toString() : v)}`);
        const contract = getContract({
            client: this.client,
            address: collection,
            chain: this.web3Config.targetChain
        });

        await this.waitSmartWalletConnection();
        const transaction = burnBatch({
            contract,
            account: this.smartWalletAddress as `0x${string}`,
            ids: [nft.id],
            values: [BigInt(1)]
        });

        const transactionResult = await sendTransaction({
            account: this.smartAccount,
            transaction
        });
        this.logger.debug(`Transaction result: ${JSON.stringify(transactionResult, (_, v) => typeof v === 'bigint' ? v.toString() : v)}`);

        return transactionResult?.transactionHash;
    }

    private async onAccountChanged(account: Account): Promise<void> {
        this.logger.debug(`BaseInAppWallet account changed event, account: ${account}`);
        if (account == null && this.connected) {
            await this.disconnect();
        }
    }

    private async onAccountsChanged(accounts: Array<string>): Promise<void> {
        this.logger.debug(`BaseInAppWallet accounts changed event, accounts: ${accounts}`);
        if ((accounts == null || accounts.length === 0) && this.connected) {
            await this.disconnect();
        }
    }

    private onChainChanged(chain: Chain): void {
        this.logger.debug(`BaseInAppWallet chain changed event, new chain: ${chain?.name} (${chain?.id})`);
        this.onDataChanged.emit();
    }

    private async onDisconnect(): Promise<void> {
        this.logger.debug("BaseInAppWallet disconnect event");
        if (this.connected) {
            await this.disconnect();
        }
    }

    private isTargetChain(chain: Chain): boolean {
        return chain === this.web3Config.targetChain
    }
}